import React, { useState, useEffect, useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Hero Component
 * @param {Array} slides - Array of slide objects [{ id, image, title, subtitle, buttonText, buttonLink }]
 * @param {number} interval - Time (ms) before auto-advancing slides (default: 5000)
 * @param {string} height - Height of hero section (default: '380px')
 * @param {string} margin - Margin around hero section (default: '50px')
 * @param {string} borderRadius - Border radius (default: '35px')
 */
const Hero = ({
    slides = [],
    interval = 5000,
    height = "250px",
    margin = "50px",
    borderRadius = "155px",
    hidden,
}) => {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [isAnimating, setIsAnimating] = useState(false);
    const heroRef = useRef(null);
    const [parallax, setParallax] = useState(0);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    // Auto-advance slides
    useEffect(() => {
        if (!slides || slides.length <= 1) return; // nothing to rotate
        const timer = setInterval(() => {
            setCurrentSlide((prev) => (prev + 1) % slides.length);
        }, interval);
        return () => clearInterval(timer);
    }, [interval, slides]);

    // Handle responsive layout
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Parallax scroll effect
    useEffect(() => {
        let rafId = null;

        const handleScroll = () => {
            if (!heroRef.current) return;
            const rect = heroRef.current.getBoundingClientRect();
            const viewportHeight = window.innerHeight;
            const elementCenter = rect.top + rect.height / 2;
            const distanceFromCenter = elementCenter - viewportHeight / 2;

            // Adjust parallax intensity (smaller number = more subtle effect)
            const parallaxIntensity = 0.15;
            const offset = distanceFromCenter * parallaxIntensity;

            if (rafId) cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(() => setParallax(offset));
        };

        // initial position
        handleScroll();
        window.addEventListener("scroll", handleScroll, { passive: true });
        window.addEventListener("resize", handleScroll);

        return () => {
            window.removeEventListener("scroll", handleScroll);
            window.removeEventListener("resize", handleScroll);
            if (rafId) cancelAnimationFrame(rafId);
        };
    }, [heroRef]);

    const nextSlide = () => {
        if (isAnimating || !slides || slides.length === 0) return;
        setIsAnimating(true);
        setCurrentSlide((prev) => (prev + 1) % slides.length);
        setTimeout(() => setIsAnimating(false), 500);
    };

    const prevSlide = () => {
        if (isAnimating || !slides || slides.length === 0) return;
        setIsAnimating(true);
        setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
        setTimeout(() => setIsAnimating(false), 500);
    };

    // goToSlide removed — navigation handled with prev/next and dots are not used in this component

    if (!slides.length) {
        return (
            <div className="text-center py-20 text-gray-600">
                No slides provided for Hero component.
            </div>
        );
    }

    return (
        <section
            id="hero"
            className="relative overflow-hidden shadow-lg"
            style={{
                height: "350px",
                marginTop: "110px",
                marginLeft: isMobile ? "0" : margin,
                marginRight: isMobile ? "0" : margin,
                borderRadius: isMobile ? "0" : borderRadius,
            }}
        >
            {/* Slides */}
            {slides.map((slide, index) => (
                <div
                    key={slide.id}
                    className={`absolute inset-0 transition-opacity duration-700 ${index === currentSlide ? "opacity-100 z-10" : "opacity-0 z-0"
                        }`}
                >
                        <div
                            className="absolute inset-0 bg-cover bg-center transition-transform z-0"
                            style={{
                                backgroundImage: `url("${slide.image}")`,
                                transform: `scale(1.1) translateY(${parallax}px)`,
                                borderRadius: isMobile ? '0' : borderRadius,
                            }}
                        />

                        <div
                            aria-hidden="true"
                            className="absolute inset-0 bg-black/25 pointer-events-none z-10"
                            style={{ borderRadius: isMobile ? '0' : borderRadius }}
                        />

                        <div className="relative z-20 h-full flex items-center justify-start px-10">
                        <div
                            className={`bg-white rounded-xl shadow-xl border border-gray-200 p-6 max-w-md transition-all duration-700 ${index === currentSlide
                                    ? "translate-y-0 opacity-100"
                                    : "translate-y-8 opacity-0"
                                }`}
                        >
                            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">
                                {slide.title}
                            </h2>

                            <p className="hidden md:block text-sm md:text-base text-gray-600 mb-4">
                                {slide.subtitle}
                            </p>

                            {slide.showButton && !hidden && (
                                <a
                                    href={slide.buttonLink}
                                    className="inline-block bg-green-600 text-white px-5 py-2 rounded-md hover:bg-green-700 transition-colors"
                                >
                                    {slide.buttonText}
                                </a>
                            )}
                        </div>
                    </div>

                </div>
            ))}

            {/* Navigation Arrows */}
            {/* <button
                onClick={prevSlide}
                disabled={isAnimating}
                className="hidden md:block absolute left-4 top-1/2 -translate-y-1/2 z-20 bg-white bg-opacity-20 hover:bg-opacity-30 text-white p-3 rounded-full transition-all disabled:opacity-50"
                aria-label="Previous slide"
            >
                <ChevronLeft className="w-6 h-6" />
            </button>
            <button
                onClick={nextSlide}
                disabled={isAnimating}
                className="hidden md:block absolute right-4 top-1/2 -translate-y-1/2 z-20 bg-white bg-opacity-20 hover:bg-opacity-30 text-white p-3 rounded-full transition-all disabled:opacity-50"
                aria-label="Next slide"
            >
                <ChevronRight className="w-6 h-6" />
            </button> */}


        </section>
    );
};

export default Hero;
